import createAsyncActions from 'shared/redux/helpers/create-async-actions';
import vodAdapter from 'shared/utils/adapters/vod';
import uploadingStatuses from 'shared/constants/uploading-statuses';

import { normalizeVideo } from 'shared/redux/helpers/normalized';
import { subscribeOnProgressUpdate, subscribeOnAsyncUpdates } from 'dashboard/utils/progress-update-utils';

import { toFakeVideoData, addDataToSameChannelVideos } from 'dashboard/redux/helpers/upload';


const ACTIONS = createAsyncActions('SERVER.VIDEO.UPLOAD', ['START', 'ITEM_CREATED', 'PROGRESS', 'SUCCESS', 'FAIL']);
export const NAMES = ACTIONS.NAMES;
const { START, ITEM_CREATED, PROGRESS, SUCCESS, FAIL } = ACTIONS;

function createNewItem(response, state, fakeVideoData, dispatch, analytics) {
  response = {
    ...response,
    // mark item as newly created
    data: normalizeVideo({ ...response.data, isNew: true }),
  };
  addDataToSameChannelVideos(response.data, state);

  dispatch(SUCCESS(fakeVideoData, response, {
    analytics: {
      type: 'bi',
      payload: analytics,
    },
  }));
  return response;
}

function handleError(response, state, videoId, fakeVideoData, dispatch, analytics) {
  const { status, error } = response;
  const data = normalizeVideo({
    id: videoId,
    error,
    uploadingStatus: uploadingStatuses.FAILED,
  });
  addDataToSameChannelVideos(data, state);
  dispatch(FAIL(fakeVideoData, { ...response, data }, {
    analytics: _.filter([
      { type: 'bi', payload: { response, ...analytics } },
      { // for NewRelic
        type: 'detailed-error',
        payload: { name: 'upload-error', errorData: response },
      },
      status > 500 || !error ?
        {
          type: 'error',
          payload: { name: 'dashboard.video.upload' },
        } :
        null,
    ]),
  }));
  return Promise.reject({ ...response, data });
}

function upload(file, uiAnalytics) {
  return function (dispatch, getState) {
    const uploadKey = Symbol('upload-key');
    const analytics = { uploadKey, file, ...uiAnalytics };
    const fakeVideoData = toFakeVideoData(file);

    const { vodSettings } = getState();
    const { defaultChannelId } = vodSettings;

    const uploadPromise = vodAdapter.item.upload({
      file,
      title: fakeVideoData.title,
      listId: defaultChannelId,
    });

    const { on, off } = uploadPromise;
    _.assign(fakeVideoData, { on, off });

    dispatch(START(fakeVideoData, { data: normalizeVideo(fakeVideoData) },
      { analytics: { type: 'bi', payload: { uploadPromise, ...analytics } } }));

    let videoId = fakeVideoData.id;
    on(vodAdapter.events.ITEM.CREATE.SUCCESS, function (response) {
      videoId = response.data.id;
      response = {
        ...response,
        data: normalizeVideo({ ...fakeVideoData, ...response.data }),
      };
      dispatch(ITEM_CREATED(fakeVideoData, response));
    });


    subscribeOnProgressUpdate(uploadPromise, function ({ uploadingStatus }) {
      // TODO: move to video and channelVideo reducers, subscribed to all ACTIONS
      const data = normalizeVideo({
        id: videoId,
        uploadingStatus,
      });
      addDataToSameChannelVideos(data, getState());

      dispatch(PROGRESS(fakeVideoData, { data }));
    });


    return uploadPromise
      .then(
        response => createNewItem(response, getState(), fakeVideoData, dispatch, analytics),
        error => handleError(error, getState(), videoId, fakeVideoData, dispatch, analytics)
      );
  };
}

function uploadByUrl(file, uiAnalytics) {
  return function (dispatch, getState) {
    const uploadKey = Symbol('upload-key');
    const analytics = { uploadKey, file, ...uiAnalytics };
    const fakeVideoData = toFakeVideoData(file);

    const { vodSettings } = getState();
    const { defaultChannelId } = vodSettings;

    const uploadPromise = vodAdapter.item.uploadByUrl({
      file,
      title: fakeVideoData.title,
      listId: defaultChannelId,
    });


    const { on, off } = uploadPromise;
    _.assign(fakeVideoData, { on, off });

    dispatch(START(fakeVideoData, { data: normalizeVideo(fakeVideoData) },
      { analytics: { type: 'bi', payload: { uploadPromise, ...analytics } } }));

    let videoId = fakeVideoData.id;
    on(vodAdapter.events.ITEM.CREATE.SUCCESS, function (response) {
      videoId = response.data.id;
      response = {
        ...response,
        data: normalizeVideo({ ...fakeVideoData, ...response.data }),
        uploadingStatus: uploadingStatuses.UPLOADING,
      };
      dispatch(ITEM_CREATED(fakeVideoData, response));
    });


    let unsubscribeFn;

    return uploadPromise
      .then(({ videoFile, itemResponse }) => {
        const fileId = _.get(videoFile, 'data.id');

        return new Promise((resolve, reject) => {
          unsubscribeFn = subscribeOnAsyncUpdates(fileId, ({ uploadingStatus }) => {
            const data = normalizeVideo({
              id: videoId,
              uploadingStatus,
            });
            addDataToSameChannelVideos(data, getState());

            dispatch(PROGRESS(fakeVideoData, { data }));

            if (uploadingStatus === uploadingStatuses.SUCCEEDED) {
              // video file successfully uploaded
              unsubscribeFn();
              resolve(itemResponse);
            } else if (uploadingStatus === uploadingStatuses.FAILED) {
              // something went wrong
              unsubscribeFn();

              reject({
                data: {
                  result: fileId,
                  error: uploadingStatuses.FAILED,
                },
              });
            }
          });
        });
      })
      // upload updated item
      .then(itemResponse => vodAdapter.item.get(itemResponse.data.id))
      .then(
        response => createNewItem(response, getState(), fakeVideoData, dispatch, analytics),
        error => handleError(error, getState(), videoId, fakeVideoData, dispatch, analytics)
      );
  };
}

export {
  upload,
  uploadByUrl,
};



// WEBPACK FOOTER //
// ./src/dashboard/redux/server/actions/video/upload.js